import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { UploaderError } from './use-image-uploader';
import { useUserGeneratedContent } from '../../../../models/user-generated-contents/context';
import ContentSubmissionService from '../../../../services/content-submission';
import type {
  ContentLink,
  MediaGroup,
} from '../../../../models/user-generated-contents/types';

export const MEDIA_GROUP_TYPE = 'links' as const;
export const MAX_LINKS = 5;

export type UseLinkUploaderReturn = {
  links: ContentLink[];
  isInputOpen: boolean;
  isFetching: boolean;
  totalCount: number;
  isAtLimit: boolean;
  errors: UploaderError[];
  clearErrors: () => void;
  openInput: () => void;
  closeInput: () => void;
  handleAdd: (url: string) => Promise<void>;
  handleDelete: (index: number) => void;
  handleReorder: (links: ContentLink[]) => void;
};

// Loose URL guard. We let the backend's preview fetch be the authoritative
// validator; this just catches obvious typos before paying the round-trip.
// Accepts bare hosts like "nasa.gov" — handleAdd prepends https:// before
// calling the preview endpoint.
const URL_PATTERN = /^(https?:\/\/)?[^\s./]+\.[^\s]+/i;

// Prepend https:// when no scheme is present so users can type "nasa.gov".
const normalizeUrl = (url: string): string => {
  if (/^https?:\/\//i.test(url)) return url;
  return `https://${url}`;
};

const hostnameFromUrl = (url: string): string => {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
};

const useLinkUploader = (): UseLinkUploaderReturn => {
  const { t } = useTranslation();
  const { userGeneratedContent, updateAttachmentGroup } =
    useUserGeneratedContent();

  const [isInputOpen, setIsInputOpen] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [errors, setErrors] = useState<UploaderError[]>([]);

  const attachments = userGeneratedContent.attachments ?? [];
  const linksGroup = attachments.find(
    (g): g is Extract<MediaGroup, { type: typeof MEDIA_GROUP_TYPE }> =>
      g.type === MEDIA_GROUP_TYPE
  );
  const links: ContentLink[] = linksGroup?.items ?? [];

  const totalCount = links.length;
  const isAtLimit = totalCount >= MAX_LINKS;

  const openInput = useCallback(() => {
    if (isAtLimit) {
      setErrors([
        {
          title: '',
          description: t(
            'user_generated_content.media.links.error_too_many_links',
            { max: MAX_LINKS }
          ),
        },
      ]);
      return;
    }
    setErrors([]);
    setIsInputOpen(true);
  }, [isAtLimit, t]);

  const closeInput = useCallback(() => {
    setIsInputOpen(false);
    setErrors([]);
  }, []);

  const handleAdd = useCallback(
    async (url: string) => {
      const trimmed = url.trim();
      if (!URL_PATTERN.test(trimmed)) {
        setErrors([
          {
            title: '',
            description: t(
              'user_generated_content.media.links.error_invalid_url'
            ),
          },
        ]);
        return;
      }

      if (isAtLimit) {
        setErrors([
          {
            title: '',
            description: t(
              'user_generated_content.media.links.error_too_many_links',
              { max: MAX_LINKS }
            ),
          },
        ]);
        return;
      }

      const normalized = normalizeUrl(trimmed);

      setIsFetching(true);
      setErrors([]);
      try {
        // The id is irrelevant for link previews (not scoped to a submission)
        const service = new ContentSubmissionService('');
        const { data } = await service.fetchLinkPreview(normalized);
        const newLink: ContentLink = {
          url: data.url || normalized,
          title: data.title || hostnameFromUrl(normalized),
          description: data.description || '',
          image_url: data.image_url || null,
          images: data.image_url ? [{ url: data.image_url }] : [],
        };
        updateAttachmentGroup(MEDIA_GROUP_TYPE, (prev) => [
          ...prev,
          { ...newLink, order: prev.length },
        ]);
        setIsInputOpen(false);
      } catch {
        // Add a text-only card so the user isn't blocked when Orare misses
        // (paywalled URLs, sites that block scrapers, etc.). They can still
        // delete + retry if they want different metadata.
        const newLink: ContentLink = {
          url: normalized,
          title: hostnameFromUrl(normalized),
          description: '',
          image_url: null,
          images: [],
        };
        updateAttachmentGroup(MEDIA_GROUP_TYPE, (prev) => [
          ...prev,
          { ...newLink, order: prev.length },
        ]);
        setIsInputOpen(false);
      } finally {
        setIsFetching(false);
      }
    },
    [isAtLimit, t, updateAttachmentGroup]
  );

  const handleDelete = useCallback(
    (index: number) => {
      updateAttachmentGroup(MEDIA_GROUP_TYPE, (prev) => {
        const next = prev
          .filter((_, i) => i !== index)
          .map((l, i) => ({ ...l, order: i }));
        return next.length > 0 ? next : undefined;
      });
      setErrors([]);
    },
    [updateAttachmentGroup]
  );

  const handleReorder = useCallback(
    (reordered: ContentLink[]) => {
      updateAttachmentGroup(MEDIA_GROUP_TYPE, () =>
        reordered.map((l, i) => ({ ...l, order: i }))
      );
      setErrors([]);
    },
    [updateAttachmentGroup]
  );

  const clearErrors = useCallback(() => setErrors([]), []);

  return {
    links,
    isInputOpen,
    isFetching,
    totalCount,
    isAtLimit,
    errors,
    clearErrors,
    openInput,
    closeInput,
    handleAdd,
    handleDelete,
    handleReorder,
  };
};

export default useLinkUploader;
